/**
 * The one place that talks to `localStorage`, and the one place that survives it
 * throwing.
 *
 * Storage is not reliably available. Safari in private browsing, a browser with
 * site data blocked, and an iframe with third-party storage partitioned all
 * throw from `getItem` rather than returning null — and the two callers here run
 * during boot, before anything has drawn. An exception there is a black page.
 *
 * So every read answers with a fallback and every write is allowed to fail
 * silently. **A player whose browser refuses to remember anything still gets a
 * complete game**; they get it from the beginning every time, which is the
 * behaviour the whole project had until this session anyway.
 *
 * `no-console` is an error in this repo (D-005) and a swallowed exception is
 * exactly the kind of thing that would tempt a `console.warn`. It does not get
 * one: the failure is visible in the product — the title screen offers no
 * Continue — and that is a better signal than a line in a console nobody has
 * open on a phone.
 */

/** Read a stored string, or `null` if it is absent *or* unreadable. */
export function readRaw(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Write a stored string. Silently does nothing where storage is unavailable. */
export function writeRaw(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Deliberately empty; see the header.
  }
}

/** Remove a stored key. Silently does nothing where storage is unavailable. */
export function removeRaw(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Deliberately empty; see the header.
  }
}

/**
 * Parse stored JSON into a record, or `null` if it is absent, unreadable, not
 * JSON, or not an object.
 *
 * **Every field is then read defensively by the caller**, because the thing on
 * the other side of this function is a string a player's browser has been
 * holding since a build that no longer exists. A save format that trusts its own
 * past output is a save format that crashes the first time it is changed.
 */
export function readJson(key: string): Readonly<Record<string, unknown>> | null {
  const raw = readRaw(key);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    return parsed as Readonly<Record<string, unknown>>;
  } catch {
    return null;
  }
}
